export type CompiledTemplate = (data: Record<string, unknown>) => string;

interface TemplatePart {
  type: "text" | "expr";
  value: string;
}

/**
 * Brace-aware parser that splits a template source string into
 * static text segments and `${...}` expression segments.
 * Handles nested braces, template literals, and string escapes.
 *
 * Nesting-aware: `${...}` expressions inside nested `<data-frame>`
 * tags are treated as literal text (they belong to the child frame).
 */
export function parseTemplateParts(source: string): TemplatePart[] {
  const parts: TemplatePart[] = [];
  let i = 0;
  let textStart = 0;
  let nestDepth = 0;

  function flushText(end: number): void {
    if (end > textStart) {
      parts.push({ type: "text", value: source.slice(textStart, end) });
    }
    textStart = end;
  }

  while (i < source.length) {
    if (source.charCodeAt(i) === 60 /* < */) {
      if (source.startsWith("</data-frame", i)) {
        const gt = source.indexOf(">", i + 12);
        if (gt !== -1 && nestDepth > 0) {
          nestDepth--;
        }
        i++;
        continue;
      }
      if (source.startsWith("<data-frame", i)) {
        const after = i + 11 < source.length ? source.charCodeAt(i + 11) : -1;
        if (after === 32 || after === 9 || after === 10 || after === 13 || after === 62 || after === -1) {
          nestDepth++;
        }
      }
      i++;
      continue;
    }

    if (nestDepth > 0) {
      i++;
      continue;
    }

    if (
      source.charCodeAt(i) === 36 /* $ */ &&
      i + 1 < source.length &&
      source.charCodeAt(i + 1) === 123 /* { */
    ) {
      flushText(i);

      let j = i + 2;
      let depth = 1;

      while (j < source.length && depth > 0) {
        const ch = source[j];

        if (ch === "`") {
          j++;
          while (j < source.length && source[j] !== "`") {
            if (source[j] === "\\" && j + 1 < source.length) {
              j += 2;
            } else if (source[j] === "$" && j + 1 < source.length && source[j + 1] === "{") {
              j += 2;
              let inner = 1;
              while (j < source.length && inner > 0) {
                const ic = source[j];
                if (ic === "'" || ic === '"') {
                  const q = ic;
                  j++;
                  while (j < source.length && source[j] !== q) {
                    if (source[j] === "\\" && j + 1 < source.length) j++;
                    j++;
                  }
                  if (j < source.length) j++;
                } else if (ic === "`") {
                  j++;
                  while (j < source.length && source[j] !== "`") {
                    if (source[j] === "\\" && j + 1 < source.length) j += 2;
                    else j++;
                  }
                  if (j < source.length) j++;
                } else {
                  if (ic === "{") inner++;
                  else if (ic === "}") inner--;
                  j++;
                }
              }
            } else {
              j++;
            }
          }
          if (j < source.length) j++;
          continue;
        }

        if (ch === '"' || ch === "'") {
          const quote = ch;
          j++;
          while (j < source.length && source[j] !== quote) {
            if (source[j] === "\\" && j + 1 < source.length) j++;
            j++;
          }
          if (j < source.length) j++;
          continue;
        }

        if (ch === "{") depth++;
        else if (ch === "}") depth--;
        j++;
      }

      const expr = source.slice(i + 2, j - 1).trim();
      if (expr) {
        parts.push({ type: "expr", value: decodeHTMLEntities(expr) });
      }
      textStart = j;
      i = j;
      continue;
    }

    i++;
  }

  flushText(source.length);
  return parts;
}

function coerce(value: unknown): string {
  if (value === false || value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(coerce).join("");
  return String(value);
}

const ENTITY_MAP: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&#x27;": "'",
  "&#x2F;": "/",
};

const ENTITY_RE = /&(?:amp|lt|gt|quot|#39|#x27|#x2F);/g;

function decodeHTMLEntities(str: string): string {
  return str.replace(ENTITY_RE, (match) => ENTITY_MAP[match] ?? match);
}

/**
 * Compiles a template source string into a reusable render function.
 * Expressions are evaluated with `with(data)` so data properties are
 * accessible as bare identifiers. Globals (window, Math, etc.) remain
 * accessible since `with` falls through to the enclosing scope.
 *
 * Falsy values (false, null, undefined) render as empty string so that
 * conditional patterns like `${condition && '<p>html</p>'}` work.
 * Arrays are auto-joined so `.map()` works without `.join('')`.
 */
export function compileLiteral(source: string): CompiledTemplate {
  const parts = parseTemplateParts(source);

  if (parts.length === 0) {
    return () => "";
  }

  if (parts.every((p) => p.type === "text")) {
    const staticResult = parts.map((p) => p.value).join("");
    return () => staticResult;
  }

  const body = parts
    .map((part) => {
      if (part.type === "text") {
        return JSON.stringify(part.value);
      }
      return `__coerce__(${part.value})`;
    })
    .join("+");

  const fn = new Function(
    "__data__",
    "__coerce__",
    `with(__data__){return ${body};}`
  ) as (data: Record<string, unknown>, coerceFn: typeof coerce) => string;

  return (data: Record<string, unknown>) => fn(data, coerce);
}

/**
 * Compile an event handler expression into a callable function.
 * The expression runs inside `with(__proxy__)` so data properties
 * are accessible as bare identifiers via the reactive proxy.
 * Mutations on the proxy auto-trigger re-renders.
 */
export function compileHandler(
  expr: string
): (event: Event, el: Element, frame: Element, parent: Element | null, proxy: Record<string, unknown>) => void {
  const fn = new Function(
    "$event",
    "$el",
    "$frame",
    "$parent",
    "__proxy__",
    `with(__proxy__){${expr}}`
  ) as (event: Event, el: Element, frame: Element, parent: Element | null, proxy: Record<string, unknown>) => void;
  return fn;
}

const DATA_FRAME_TAG = "DATA-FRAME";

/**
 * Parse data from a single element's `data` attribute and `data-*`
 * dataset entries. The `data` attribute supports both JSON and JS
 * object literal syntax (e.g. `{count: 0, name: "hello"}`).
 *
 * The `data` attribute values take precedence over dataset entries.
 * Dataset keys matching `data-on-*` (event handlers) are excluded.
 */
const INTERNAL_DATASET_KEYS = new Set(["state"]);

export function parseData(el: Element): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (el instanceof HTMLElement && el.dataset) {
    for (const [k, v] of Object.entries(el.dataset)) {
      if (/^on[A-Z]/.test(k)) continue;
      if (INTERNAL_DATASET_KEYS.has(k)) continue;
      result[k] = v;
    }
  }

  const raw = el.getAttribute("data");
  if (raw) {
    let parsed: unknown;
    try {
      parsed = new Function(`return(${raw})`)();
    } catch {
      // invalid expression, skip
    }
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      Object.assign(result, parsed as Record<string, unknown>);
    }
  }

  return result;
}

/**
 * Walk up the DOM from `el`, collecting data from every ancestor
 * `<data-frame>` element, then merge with `el`'s own data.
 * Ancestors are merged top-down: root ancestor first, closest ancestor
 * overrides, self wins over all.
 *
 * When `excludeSelf` is true, the element itself is skipped from the
 * cascade. This is used by the component to combine ancestor data
 * with its own `_localData` (which may contain proxy mutations not
 * reflected in the DOM attribute).
 */
export function cascadeData(el: Element, excludeSelf = false): Record<string, unknown> {
  const chain: Element[] = [];
  let cur: Element | null = el;

  while (cur) {
    if (cur.tagName === DATA_FRAME_TAG && !(excludeSelf && cur === el)) {
      chain.unshift(cur);
    }
    cur = cur.parentElement;
  }

  const acc: Record<string, unknown> = {};
  for (const ancestor of chain) {
    Object.assign(acc, parseData(ancestor));
  }

  return acc;
}
